import { useEffect, useMemo, useState } from "react";
import {
  format,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subWeeks,
  subMonths,
  subQuarters,
} from "date-fns";
import { supabase } from "../../lib/supabase";
import type { Deal } from "../../lib/types";

import DealLink from "../DealLink";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

const STAGE_CHIP: Record<string, string> = {
  drone_complete: "bg-emerald-100 text-emerald-700",
  report_in_progress: "bg-sky-100 text-sky-700",
  qc_review: "bg-indigo-100 text-indigo-700",
  report_approved: "bg-teal-100 text-teal-700",
  insufficient_damage: "bg-slate-100 text-slate-600",
};

const STAGE_LABEL: Record<string, string> = {
  drone_complete: "Drone complete → Jeremy",
  report_in_progress: "With Jeremy",
  qc_review: "QC (Jennifer)",
  report_approved: "Approved → ASM gate",
  insufficient_damage: "Insufficient damage",
};

type FilterKey =
  | "today"
  | "yesterday"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "last_30"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "all_time";

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_30", label: "Last 30 days" },
  { key: "this_quarter", label: "This quarter" },
  { key: "last_quarter", label: "Last quarter" },
  { key: "this_year", label: "This year" },
  { key: "all_time", label: "All time" },
];

function getDateRange(filter: FilterKey): { from: string | null; to: string | null } {
  const now = new Date();
  const todayStart = startOfDay(now);

  switch (filter) {
    case "today":
      return { from: todayStart.toISOString(), to: null };
    case "yesterday": {
      const yStart = startOfDay(subDays(now, 1));
      return { from: yStart.toISOString(), to: todayStart.toISOString() };
    }
    case "this_week":
      return { from: startOfWeek(now, { weekStartsOn: 1 }).toISOString(), to: null };
    case "last_week": {
      const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 });
      const lastWeekStart = subWeeks(thisWeekStart, 1);
      return { from: lastWeekStart.toISOString(), to: thisWeekStart.toISOString() };
    }
    case "this_month":
      return { from: startOfMonth(now).toISOString(), to: null };
    case "last_month": {
      const thisMonthStart = startOfMonth(now);
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      return { from: lastMonthStart.toISOString(), to: thisMonthStart.toISOString() };
    }
    case "last_30":
      return { from: subDays(now, 30).toISOString(), to: null };
    case "this_quarter":
      return { from: startOfQuarter(now).toISOString(), to: null };
    case "last_quarter": {
      const thisQStart = startOfQuarter(now);
      const lastQStart = startOfQuarter(subQuarters(now, 1));
      return { from: lastQStart.toISOString(), to: thisQStart.toISOString() };
    }
    case "this_year":
      return { from: startOfYear(now).toISOString(), to: null };
    case "all_time":
      return { from: null, to: null };
  }
}

function formatAddress(d: Deal) {
  return [
    d.property_street,
    [d.property_city, d.property_state].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(" · ");
}

export default function CompletedTab() {
  const [filter, setFilter] = useState<FilterKey>("this_week");
  const [deals, setDeals] = useState<Deal[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => getDateRange(filter), [filter]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      setDeals(null);

      let query = supabase
        .from("deals")
        .select(
          "id,homeowner_name,property_street,property_city,property_state,property_zip,claim_number,stage,phase,drone_completed_date,drone_sla_met,imging_report_link,report_qc_status,assigned_drone_pilot_id",
        )
        .not("drone_completed_date", "is", null)
        .order("drone_completed_date", { ascending: false });

      if (range.from) query = query.gte("drone_completed_date", range.from);
      if (range.to) query = query.lt("drone_completed_date", range.to);

      const { data, error } = await query;
      if (error) {
        if (!cancelled) setError(error.message);
        return;
      }
      if (!cancelled) setDeals((data ?? []) as unknown as Deal[]);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to]);

  if (error)
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded">
        Couldn't load completed — {error}
      </div>
    );

  return (
    <div>
      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={
              "px-3 py-1.5 rounded-full text-xs font-medium transition-colors " +
              (filter === f.key
                ? "bg-bardin-teal text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Count */}
      {deals && (
        <div className="mb-3 text-sm text-slate-600">
          {deals.length} {deals.length === 1 ? "flight" : "flights"}
        </div>
      )}

      {/* Loading */}
      {!deals && (
        <div className="text-slate-500 p-4">Loading completed flights…</div>
      )}

      {/* Empty state */}
      {deals && deals.length === 0 && (
        <div className="bg-white rounded-xl shadow p-8 text-center text-slate-500">
          No completed drone flights for this period.
        </div>
      )}

      {/* Results */}
      {deals && deals.length > 0 && (
        <ul className="space-y-2">
          {deals.map((d) => (
            <li key={d.id} className="bg-white rounded-lg shadow-sm px-4 py-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {d.stage && (
                      <span
                        className={
                          "text-xs font-medium px-2 py-0.5 rounded " +
                          (STAGE_CHIP[d.stage] ?? "bg-slate-100 text-slate-600")
                        }
                      >
                        {STAGE_LABEL[d.stage] ?? d.stage}
                      </span>
                    )}
                    {d.drone_completed_date && (
                      <span className="text-xs text-slate-500">
                        Flown{" "}
                        {format(
                          new Date(d.drone_completed_date),
                          "MMM d, yyyy",
                        )}
                      </span>
                    )}
                    {d.drone_sla_met === false && (
                      <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                        SLA missed
                      </span>
                    )}
                    {d.claim_number && (
                      <span className="text-xs text-slate-400">
                        #{d.claim_number}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-bardin-ink">
                    <DealLink id={d.id}>
                      {d.homeowner_name ?? "(no name)"}
                    </DealLink>
                  </div>
                  <div className="text-sm text-slate-600">
                    {isAddressLinkable(d) ? (
                      <a
                        href={mapsHref(d)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="hover:underline hover:text-slate-800"
                      >
                        {formatAddress(d)}
                      </a>
                    ) : (
                      formatAddress(d) || "(no address)"
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {d.imging_report_link && (
                    <a
                      href={d.imging_report_link}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 text-sm hover:bg-slate-200"
                    >
                      IMGING report
                    </a>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
